import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import AuthService from '../services/authService';
import FriendsServiceDB from '../services/friendsServiceDB';
import { friendMatchesQuery } from '../utils/friendSearch';
import ChildProfileServiceDB from '../services/childProfileServiceDB';
import SettingsService from '../services/settingsService';
import UserAvatar from './UserAvatar';
import ChildAvatar from './shared/ChildAvatar';
import Icon from './shared/Icon';
import Modal from './shared/Modal';
import Button from './shared/Button';
import Spinner from './shared/Spinner';
import { focusStyle } from '../styles/focus';
import { weightsForTaste, tasteFromWeights, canPutKidsFirst } from '../utils/whosGoingTaste';

/**
 * Who's going — the one people picker, used wherever the app asks who is
 * coming along (search, Discover, trips, Day Out, visit ratings, day recaps).
 * Chosen by Kurtis from the Who's Going Lab, 2026-10-13 (style D): "Just me"
 * and his Quick Groups as rows on top (one tap adds a group, the arrow picks
 * some of it), then everyone else as a grid of faces.
 *
 * `context` sets the wording and whether the user is one of the people:
 *   outing — search and Discover. You are always in, and "Just me" is offered.
 *   trip   — a trip's or Day Out's travellers. You are always in; no "Just me"
 *            (one tap would drop every traveller).
 *   past   — who was with you on a visit. You are not listed.
 *   invite — who to invite to a day. You are not listed; needs one person.
 * You can never take yourself out: on a trip that deleted the owner's own
 * traveller row, and the owner then lost change notices and edit rights on
 * co-travellers' activities. For search the server blends your taste in
 * whether or not you are ticked, so the choice would do nothing there.
 *
 * `onSave(people, weights)`: people are the picked person objects (yours
 * first when included); weights is the per-person taste split for search, or
 * null for an equal share (see utils/whosGoingTaste).
 */
const CONTEXTS = {
  outing: { includeSelf: true, justMe: true },
  trip: { includeSelf: true, justMe: false },
  // Not listed, but handed back on Done: the visit form sends the list as
  // `companions`, and the server rebuilds attendees from it — it keeps the
  // organizer row only for the user's own entry, and an empty list skips the
  // rebuild (so un-tagging everyone would leave the old tags in place).
  past: { includeSelf: false, justMe: false, returnSelf: true },
  invite: { includeSelf: false, justMe: false, needsOne: true },
};

// "Whose taste counts most?" — the choices utils/whosGoingTaste translates.
const TASTE_MODES = ['equal', 'kids', 'lean'];

// The taste last saved, kept across opens and pages. Only a tie-breaker:
// with one child, "Kids first" and "One person… → that child" give the same
// weights, and this says which one the user picked (see tasteFromWeights).
let lastSavedTaste = null;

function calculateAge(birthDate) {
  if (!birthDate) return null;
  const today = new Date();
  const birth = new Date(birthDate);
  if (Number.isNaN(birth.getTime())) return null;
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function uniqueById(people) {
  const seen = new Set();
  return people.filter(p => p && p.id && !seen.has(p.id) && seen.add(p.id));
}

function PersonAvatar({ person, size }) {
  if (person.type === 'child') {
    return <ChildAvatar photoUrl={person.photoUrl} emoji={person.avatar} altName={person.name} size={size === 'face' ? 'face' : size === 'md' ? 'md' : 'xs'} />;
  }
  return <UserAvatar user={person} size={size === 'face' ? 'lg' : size === 'md' ? 'md' : 'sm'} />;
}

function RoundCheck({ on, partial }) {
  return (
    <span
      aria-hidden="true"
      className={`flex-shrink-0 w-[22px] h-[22px] rounded-full flex items-center justify-center border-[1.5px] ${
        on ? 'bg-primary border-primary' : partial ? 'bg-surface border-primary' : 'bg-surface border-border'
      }`}
    >
      {on && <Icon name="check" className="w-3.5 h-3.5 text-content-inverse" />}
      {!on && partial && <Icon name="minus" className="w-3.5 h-3.5 text-primary" />}
    </span>
  );
}

// A row in the top block: "Just me" or a Quick Group. Rows are frames on the
// sheet's surface, like Discover's OptionRow, so they read as choices.
function ChoiceRow({ selected, partial, onClick, icon, label, desc, children, expanded, onToggleExpand, expandLabel }) {
  return (
    <div className={`rounded-xl border overflow-hidden ${selected || partial ? 'border-primary bg-primary/10' : 'border-border bg-surface'}`}>
      <div className="flex items-stretch">
        <button
          type="button"
          onClick={onClick}
          aria-pressed={selected}
          className={`flex-1 min-w-0 flex items-center gap-3 text-left min-h-[64px] px-3 py-2.5 ${focusStyle}`}
        >
          {icon}
          <span className="flex-1 min-w-0 flex flex-col">
            <span className="text-[15px] font-semibold text-content-primary truncate">{label}</span>
            {desc && <span className="text-[13px] text-content-secondary truncate">{desc}</span>}
          </span>
          <RoundCheck on={selected} partial={partial} />
        </button>
        {onToggleExpand && (
          <button
            type="button"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-label={expandLabel}
            className={`w-12 flex-shrink-0 flex items-center justify-center border-l border-border text-content-secondary hover:text-content-primary ${focusStyle}`}
          >
            <Icon name="chevronDown" className={`w-[18px] h-[18px] transition-transform duration-normal ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
      {expanded && children}
    </div>
  );
}

function MemberChip({ person, selected, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`inline-flex items-center gap-1.5 min-h-[44px] sm:min-h-[36px] pl-1 pr-3 rounded-full border text-sm font-medium ${focusStyle} ${
        selected ? 'bg-primary border-primary text-content-inverse' : 'bg-surface border-border text-content-secondary hover:border-primary-light'
      }`}
    >
      <PersonAvatar person={person} size="sm" />
      <span className="max-w-[120px] truncate">{label}</span>
    </button>
  );
}

function Face({ person, selected, onClick, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex flex-col items-center gap-1.5 min-w-0 py-1 rounded-xl ${focusStyle}`}
    >
      <span
        className={`relative w-14 h-14 rounded-full bg-surface-secondary flex items-center justify-center transition-shadow duration-fast ${
          selected ? 'ring-[2.5px] ring-primary ring-offset-2 ring-offset-surface' : ''
        }`}
      >
        <PersonAvatar person={person} size="face" />
        {selected && (
          <span className="absolute -right-1 -bottom-1 w-[22px] h-[22px] rounded-full bg-primary text-content-inverse flex items-center justify-center ring-2 ring-surface">
            <Icon name="check" className="w-3 h-3" />
          </span>
        )}
      </span>
      <span className={`text-[12.5px] leading-tight text-center text-content-primary line-clamp-2 break-words max-w-full ${selected ? 'font-semibold' : ''}`}>
        {label}
      </span>
    </button>
  );
}

function SectionLabel({ children, count }) {
  return (
    <div className="flex gap-1.5 mt-5 mb-2 text-xs font-bold uppercase tracking-wide text-content-secondary">
      {children}
      {count != null && <span className="text-content-muted font-semibold">{count}</span>}
    </div>
  );
}

function WhosGoingModal({
  isOpen,
  onClose,
  onSave,
  initialSelectedPeople = [],
  initialWeights = null,
  showPriority = false,
  context = 'outing',
}) {
  const { t, i18n } = useTranslation('modals');
  const ctx = CONTEXTS[context] || CONTEXTS.outing;
  const [loaded, setLoaded] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [friends, setFriends] = useState([]);
  const [kids, setKids] = useState([]);
  const [quickGroups, setQuickGroups] = useState([]);
  const [picked, setPicked] = useState([]);
  const [taste, setTaste] = useState({ mode: 'equal', leanId: null });
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroupId, setExpandedGroupId] = useState(null);

  // The seed is read when the modal opens, not on every render: callers often
  // pass a fresh array each render, and re-seeding wiped unsaved ticks.
  const seedRef = useRef({ people: initialSelectedPeople, weights: initialWeights });
  seedRef.current = { people: initialSelectedPeople, weights: initialWeights };
  const startRef = useRef({ self: null });
  // Picked at open but in neither your friends nor your kids (a co-traveller
  // who isn't a friend, another parent's child). Fixed for the session so an
  // unticked one stays on the grid and can be ticked again.
  const [outsiders, setOutsiders] = useState([]);

  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoaded(false);
    setSearchQuery('');
    setExpandedGroupId(null);

    (async () => {
      // Independent lookups — one round-trip instead of four.
      const [user, friendsResult, childrenResult, settingsResult] = await Promise.allSettled([
        AuthService.getCurrentUser(),
        FriendsServiceDB.getFriends(),
        ChildProfileServiceDB.getChildProfilesForParent(),
        SettingsService.getUserSettings()
      ]).then(results => results.map(r => (r.status === 'fulfilled' ? r.value : null)));
      if (cancelled) return;

      const kidList = (childrenResult || []).map(child => ({
        id: child.id,
        name: child.name,
        avatar: child.avatar || '👶',
        photoUrl: child.photoUrl || null,
        bio: child.bio || '',
        interests: child.interests || [],
        type: 'child',
        age: calculateAge(child.birthDate),
        ageGroup: ChildProfileServiceDB.calculateAgeGroup(child.birthDate),
        birthDate: child.birthDate,
        relationship: 'child'
      }));

      const friendList = Array.isArray(friendsResult) ? friendsResult : [];
      const own = user ? (seedRef.current.people || []).find(p => p && p.id === user.id) || user : null;
      let seed = uniqueById(seedRef.current.people || []);
      if (user) {
        seed = seed.filter(p => p.id !== user.id);
        if (ctx.includeSelf) seed.unshift(own);
      }
      const knownIds = new Set([...kidList, ...friendList].map(p => p.id));

      setCurrentUser(user || null);
      setFriends(friendList);
      setKids(kidList);
      setQuickGroups(settingsResult?.quickGroups || []);
      setOutsiders(seed.filter(p => (!user || p.id !== user.id) && !knownIds.has(p.id)));
      setPicked(seed);
      setTaste(tasteFromWeights(seed, seedRef.current.weights, lastSavedTaste));
      startRef.current = { self: own };
      setLoaded(true);
    })();

    return () => { cancelled = true; };
  }, [isOpen, ctx.includeSelf]);

  const selfId = currentUser?.id;
  const isPicked = (id) => picked.some(p => p.id === id);
  const firstName = (p) => (p.id === selfId ? t('whosGoing.you') : (p.name || '').split(' ')[0]);
  const faceLabel = (p) => (p.type === 'child' && p.age != null ? t('whosGoing.childAge', { name: p.name, age: p.age }) : p.name);

  const toggle = (person) => {
    if (person.id === selfId && ctx.includeSelf) return;
    setPicked(prev => (prev.some(p => p.id === person.id) ? prev.filter(p => p.id !== person.id) : [...prev, person]));
  };

  // Quick Group members resolved to people; ids that no longer resolve (an
  // ex-friend, a removed child) are skipped, and an empty group isn't shown.
  const groups = useMemo(() => {
    const byId = new Map([...kids, ...friends].map(p => [p.id, p]));
    // In the group's own order ("Jess, Mia and Theo"), as set in Quick Groups.
    return quickGroups
      .map(g => ({ ...g, people: (g.members || []).map(id => byId.get(id)).filter(Boolean) }))
      .filter(g => g.people.length > 0);
  }, [quickGroups, kids, friends]);

  const toggleGroup = (group) => {
    const all = group.people.every(p => isPicked(p.id));
    setPicked(prev => (all
      ? prev.filter(p => !group.people.some(m => m.id === p.id))
      : [...prev, ...group.people.filter(m => !prev.some(p => p.id === m.id))]));
  };

  const list = (names) => {
    try {
      return new Intl.ListFormat(i18n.language, { style: 'long', type: 'conjunction' }).format(names);
    } catch {
      return names.join(', ');
    }
  };

  const groupDesc = (group) => {
    const inGroup = group.people.filter(p => isPicked(p.id));
    const missing = group.people.filter(p => !isPicked(p.id));
    if (inGroup.length > 0 && missing.length === 1 && group.people.length > 2) {
      return t('whosGoing.group.everyoneBut', { name: firstName(missing[0]) });
    }
    if (inGroup.length > 0 && missing.length > 0) {
      return t('whosGoing.group.someOf', { picked: inGroup.length, total: group.people.length, names: inGroup.map(firstName).join(', ') });
    }
    return list(group.people.map(firstName));
  };

  // Faces: your children, then friends. Anyone picked at open who is in
  // neither list leads the friends grid, so they can be taken off and put back.
  // By name or handle: the friends list carries no email (C322).
  const q = searchQuery.trim().toLowerCase();
  const matches = (p) => friendMatchesQuery(p, searchQuery);
  const others = outsiders;
  const kidFaces = kids.filter(matches);
  const friendFaces = [...others, ...friends].filter(matches);

  const justMe = !!selfId && picked.length === 1 && picked[0].id === selfId;
  const clearable = ctx.includeSelf ? picked.some(p => p.id !== selfId) : picked.length > 0;
  const clear = () => setPicked(ctx.includeSelf && selfId ? picked.filter(p => p.id === selfId) : []);

  const goingNames = picked.map(firstName);
  const goingText = goingNames.length > 5
    ? list([...goingNames.slice(0, 4), t('whosGoing.going.more', { count: goingNames.length - 4 })])
    : list(goingNames);

  const showTaste = showPriority && context === 'outing' && picked.length >= 2;
  const kidsFirstOffered = canPutKidsFirst(picked);
  const tasteMode = taste.mode === 'kids' && !kidsFirstOffered ? 'equal'
    // "One person…" stays chosen while no one is picked yet (it counts as equal
    // until someone is); it falls back only when that person leaves the group.
    : taste.mode === 'lean' && taste.leanId && !isPicked(taste.leanId) ? 'equal'
    : taste.mode;

  const handleSave = () => {
    let people = uniqueById(picked);
    let weights = null;
    if (showTaste) {
      weights = weightsForTaste(people, { mode: tasteMode, leanId: taste.leanId });
      lastSavedTaste = { mode: tasteMode, leanId: taste.leanId };
    }
    if (ctx.returnSelf && startRef.current.self) {
      people = [startRef.current.self, ...people.filter(p => p.id !== selfId)];
    }
    // selfId lets a caller drop you synchronously (Day Out keeps invitees only).
    onSave(people, weights, { selfId: selfId || null });
    onClose();
  };

  const count = picked.length;
  const doneLabel = context === 'invite'
    ? (count > 0 ? t('whosGoing.done.invite', { count }) : t('whosGoing.done.invitePlain'))
    : context === 'past'
      ? (count > 0 ? t('whosGoing.done.tagged', { count }) : t('whosGoing.done.plain'))
      : (count >= 2 ? t('whosGoing.done.people', { count }) : t('whosGoing.done.plain'));
  const goingLead = t(`whosGoing.going.lead.${context in CONTEXTS ? context : 'outing'}`);
  const title = t(`whosGoing.contexts.${context in CONTEXTS ? context : 'outing'}.title`);

  const noPeople = loaded && friends.length === 0 && kids.length === 0 && others.length === 0;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md" position="bottom" ariaLabel={title}>
      <div className="flex flex-col h-[86dvh] md:h-[min(86dvh,760px)]">
        <Modal.Header onClose={onClose}>
          <h2 className="font-display text-[22px] leading-tight font-semibold text-content-primary">{title}</h2>
          <p className="text-sm text-content-secondary mt-0.5">{t(`whosGoing.contexts.${context in CONTEXTS ? context : 'outing'}.subtitle`)}</p>
        </Modal.Header>

        <Modal.Body className="pt-2">
          {!loaded ? (
            <div className="flex items-center justify-center py-16" role="status" aria-label={t('whosGoing.loading')}>
              <Spinner size="md" />
            </div>
          ) : (
            <>
              <div className="flex items-baseline gap-2 text-sm text-content-secondary pb-2" aria-live="polite">
                {count > 0 ? (
                  <span className="min-w-0">
                    <span className="font-semibold text-content-primary">{goingLead}</span> {goingText}
                  </span>
                ) : (
                  <span>{ctx.needsOne ? t('whosGoing.going.nobodyInvite') : t('whosGoing.going.nobody')}</span>
                )}
                {clearable && (
                  <button
                    type="button"
                    onClick={clear}
                    className={`ml-auto flex-shrink-0 min-h-[44px] sm:min-h-[32px] text-sm font-semibold text-primary hover:text-primary-dark rounded ${focusStyle}`}
                  >
                    {t('whosGoing.going.clear')}
                  </button>
                )}
              </div>

              {((ctx.justMe && currentUser) || groups.length > 0) && (
                <div className="flex flex-col gap-2 pt-1">
                  {ctx.justMe && currentUser && (
                    <ChoiceRow
                      selected={justMe}
                      onClick={() => setPicked(picked.filter(p => p.id === selfId))}
                      icon={<span className="w-10 h-10 rounded-full bg-surface-secondary flex items-center justify-center flex-shrink-0"><UserAvatar user={currentUser} size="md" /></span>}
                      label={t('whosGoing.justMe.label')}
                      desc={t('whosGoing.justMe.desc')}
                    />
                  )}
                  {groups.map(group => {
                    const inCount = group.people.filter(p => isPicked(p.id)).length;
                    const all = inCount === group.people.length;
                    const expanded = expandedGroupId === group.id;
                    return (
                      <ChoiceRow
                        key={group.id}
                        selected={all}
                        partial={inCount > 0 && !all}
                        onClick={() => toggleGroup(group)}
                        icon={<span className="w-10 h-10 rounded-full bg-surface-secondary flex items-center justify-center text-xl flex-shrink-0" aria-hidden="true">{group.emoji}</span>}
                        label={group.name}
                        desc={groupDesc(group)}
                        expanded={expanded}
                        onToggleExpand={() => setExpandedGroupId(expanded ? null : group.id)}
                        expandLabel={t('whosGoing.group.adjustAriaLabel', { name: group.name })}
                      >
                        <div className="flex flex-wrap gap-1.5 px-3 pt-2.5 pb-3 border-t border-border">
                          {group.people.map(person => (
                            <MemberChip
                              key={person.id}
                              person={person}
                              selected={isPicked(person.id)}
                              onClick={() => toggle(person)}
                              label={firstName(person)}
                            />
                          ))}
                        </div>
                      </ChoiceRow>
                    );
                  })}
                </div>
              )}

              {noPeople ? (
                <div className="text-center py-8">
                  <p className="text-sm font-medium text-content-primary">{t('whosGoing.empty.heading')}</p>
                  <p className="text-sm text-content-secondary mt-1">{t('whosGoing.empty.body')}</p>
                </div>
              ) : (
                <>
                  <SectionLabel>{t('whosGoing.anyoneElse')}</SectionLabel>
                  <div className="relative">
                    <Icon name="search" className="w-4 h-4 text-content-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <input
                      id="whos-going-search"
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={t('whosGoing.searchPlaceholder')}
                      aria-label={t('whosGoing.searchPlaceholder')}
                      className={`w-full h-11 pl-9 pr-10 text-[15px] border border-border rounded-lg bg-surface-secondary text-content-primary placeholder:text-content-muted ${focusStyle}`}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        aria-label={t('whosGoing.clearSearchAriaLabel')}
                        className={`absolute right-0 top-0 w-11 h-11 flex items-center justify-center text-content-muted hover:text-content-secondary rounded-lg ${focusStyle}`}
                      >
                        <Icon name="close" className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  {kidFaces.length > 0 && (
                    <>
                      <SectionLabel>{t('whosGoing.family')}</SectionLabel>
                      <div className="grid grid-cols-4 gap-x-2 gap-y-4">
                        {kidFaces.map(person => (
                          <Face key={person.id} person={person} selected={isPicked(person.id)} onClick={() => toggle(person)} label={faceLabel(person)} />
                        ))}
                      </div>
                    </>
                  )}

                  {friendFaces.length > 0 && (
                    <>
                      <SectionLabel count={friendFaces.length}>{t('whosGoing.friends')}</SectionLabel>
                      <div className="grid grid-cols-4 gap-x-2 gap-y-4">
                        {friendFaces.map(person => (
                          <Face key={person.id} person={person} selected={isPicked(person.id)} onClick={() => toggle(person)} label={faceLabel(person)} />
                        ))}
                      </div>
                    </>
                  )}

                  {q && kidFaces.length === 0 && friendFaces.length === 0 && (
                    <p className="text-center text-sm text-content-secondary py-8">{t('whosGoing.noMatch', { query: searchQuery.trim() })}</p>
                  )}
                </>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="flex flex-col gap-3">
          {loaded && showTaste && (
            <div className="flex flex-col gap-2">
              <span id="whos-going-taste" className="text-[13px] font-semibold text-content-secondary">{t('whosGoing.taste.label')}</span>
              <div role="group" aria-labelledby="whos-going-taste" className="flex gap-0.5 p-[3px] rounded-full bg-surface-tertiary">
                {TASTE_MODES.filter(mode => mode !== 'kids' || kidsFirstOffered).map(mode => (
                  <button
                    key={mode}
                    type="button"
                    aria-pressed={tasteMode === mode}
                    onClick={() => setTaste({ mode, leanId: mode === 'lean' && isPicked(taste.leanId) ? taste.leanId : null })}
                    className={`flex-1 min-h-[44px] sm:min-h-[36px] px-1.5 rounded-full text-[13.5px] whitespace-nowrap ${focusStyle} ${
                      tasteMode === mode ? 'bg-surface text-primary font-semibold shadow-sm' : 'text-content-secondary font-medium'
                    }`}
                  >
                    {t(`whosGoing.taste.${mode}`)}
                  </button>
                ))}
              </div>
              {tasteMode === 'lean' && (
                // One scrolling row: a wrapped row grows with the group and
                // squeezes the list above it on a phone.
                <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-0.5 [scrollbar-width:none]">
                  {picked.map(person => (
                    <MemberChip
                      key={person.id}
                      person={person}
                      selected={taste.leanId === person.id}
                      onClick={() => setTaste({ mode: 'lean', leanId: person.id })}
                      label={firstName(person)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <Button size="lg" fullWidth onClick={handleSave} disabled={!loaded || (ctx.needsOne && count === 0)}>
            {doneLabel}
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}

export default WhosGoingModal;
